import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CourseVisual } from '@/components/CourseVisual';
import { isAdmin, isTeacher } from '@/utils/auth';

const PUBLIC_STUDENT_COUNT_THRESHOLD = 200;
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { useCurrency, PriceCaption } from '@/utils/CurrencyContext';
import { toast } from 'sonner';
import { BookOpen, Users, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const canSeeAllCounts = isAdmin() || isTeacher();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#163329' }}></div>
          <p style={{ color: '#5C635A' }}>Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="courses-page">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
          data-testid="back-to-home-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
        <div className="mb-12">
          <h1
            className="text-4xl md:text-5xl font-bold tracking-tight mb-4"
            style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            data-testid="courses-title"
          >
            Explore Courses
          </h1>
          <p className="text-lg" style={{ color: '#5C635A' }}>
            Discover courses taught by expert instructors
          </p>
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="h-16 w-16 mx-auto mb-4" style={{ color: '#5C635A' }} />
            <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              No courses available yet
            </h3>
            <p style={{ color: '#5C635A' }}>Check back soon for new courses!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {courses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card
                  className="course-card h-full border cursor-pointer"
                  style={{ borderColor: '#E3E1DB', backgroundColor: '#FFFFFF' }}
                  onClick={() => navigate(`/courses/${course.id}`)}
                  data-testid={`course-card-${course.id}`}
                >
                  <CourseVisual course={course} className="aspect-video rounded-t-lg" iconClassName="h-12 w-12" />
                  <CardHeader>
                    <h3
                      className="text-xl font-semibold line-clamp-2"
                      style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
                    >
                      {course.title}
                    </h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm line-clamp-3 mb-4" style={{ color: '#5C635A' }}>
                      {course.description}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1" style={{ color: '#5C635A' }}>
                        <BookOpen className="h-4 w-4" />
                        <span>{course.lesson_count || 0} lessons</span>
                      </div>
                      {(canSeeAllCounts || (course.enrollment_count || 0) >= PUBLIC_STUDENT_COUNT_THRESHOLD) && (
                        <div className="flex items-center gap-1" style={{ color: '#5C635A' }} data-testid={`course-student-count-${course.id}`}>
                          <Users className="h-4 w-4" />
                          <span>{course.enrollment_count || 0} students</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between items-center">
                    <div className="flex flex-col">
                      <span className="text-xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                        {formatPrice(course.price)}
                      </span>
                      <PriceCaption />
                    </div>
                    <Button
                      size="sm"
                      className="rounded-full"
                      style={{ backgroundColor: '#D86B47', color: '#FFFFFF' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/courses/${course.id}`);
                      }}
                      data-testid={`view-course-button-${course.id}`}
                    >
                      View Course
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage;